import React, { useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Icon from "../AppIcon";
import { cn } from "../../utils/cn";
import { BROCHURE_MODULES, type BrochureModule } from "../../data/brochureModules";
import type { BannerState } from "./SubscriptionBanner";

/**
 * Brochure overlay — the expanded surface that appears when a Free/Trial user
 * clicks the sticky banner. Covers the routed content area but stays underneath
 * the TopBar so users keep their bearings.
 *
 * Design rationale (impeccable: hierarchy + calm):
 *   - One clear headline. One clear sub. One clear primary CTA. Everything else
 *     is supporting evidence (the module grid).
 *   - Solid surface, not a scrim. The locked page peeking through a tinted
 *     overlay reads as "broken UI" — a clean wipe reads as a dedicated screen
 *     the user navigated to on purpose.
 *   - EVERY COLOUR COMES FROM THE SEMANTIC TOKENS the rest of billing uses
 *     (`bg-background`, `bg-card`, `text-foreground`, `text-muted-foreground`,
 *     `bg-primary`). This surface used to be the one exception, painted in
 *     hardcoded slate/indigo — which not only made it drift visually from its
 *     siblings but also opted it out of the per-tenant primary override, so a
 *     tenant with its own brand colour saw indigo here and its own colour
 *     everywhere else. Module icon chips keep their per-module accent tints,
 *     which come from `data/brochureModules.ts`.
 *   - Focus is trapped inside the overlay while it's open; ESC closes; the
 *     close button is large and unambiguous. While CLOSED the whole subtree is
 *     `invisible` + `inert`, which is what actually keeps it out of the tab
 *     order — see the effect below.
 */

const TOPBAR_HEIGHT_PX = 64; // matches h-16 on TopBar.tsx
const BANNER_HEIGHT_PX = 44; // matches h-11 on the sticky banner

/**
 * Hero CTAs. Declared once because the three of them are the same button wearing different copy,
 * and three copies of a class list is three chances to drift.
 *
 * These are raw `<button>`s rather than the shared `Button` primitive on purpose: the overlay
 * renders outside the normal page shell and wanted a slightly larger touch target than
 * `Button size="default"` gives. They are otherwise held to the same token vocabulary, so a
 * tenant's primary colour reaches them like it reaches every other CTA in the app.
 */
const PRIMARY_CTA_CLASS = cn(
  "inline-flex min-h-11 items-center gap-2 rounded-lg px-5 py-2.5",
  "bg-primary text-sm font-semibold text-primary-foreground shadow-sm",
  "hover:bg-primary/90 hover:shadow",
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2",
  "transition-colors"
);

/** Escape hatches to a human. Declared as data so the separators stay consistent as links change. */
const FOOTER_LINKS: ReadonlyArray<{ href: string; labelKey: string }> = [
  { href: "https://wa.me/971547888992", labelKey: "Chat on WhatsApp" },
  { href: "https://mowazaf.com/contact-us/", labelKey: "Contact sales" },
  { href: "https://mowazaf.com/pricing/", labelKey: "See pricing" },
];

const QUIET_CTA_CLASS = cn(
  "inline-flex min-h-11 items-center gap-2 rounded-lg px-4 py-2.5",
  "text-sm font-medium text-muted-foreground",
  "hover:bg-accent hover:text-foreground",
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2",
  "transition-colors"
);

interface BrochureOverlayProps {
  open: boolean;
  onClose: () => void;
  state: BannerState;
  /**
   * Whether the viewer is Hr/Admin. Billing is their job: `/settings/billing` is role-protected and
   * `POST /request-upgrade` is Hr/Admin-only, so for everyone else both CTAs below are replaced by
   * the nudge — the same substitution `SubscriptionBanner` already makes for its inline CTA.
   */
  canManageBilling: boolean;
  /** CO-227 — was `onStartTrial`; navigates to the billing page's plan picker. */
  onSeePlans: (e?: React.SyntheticEvent) => void;
  onRequestUpgrade: (e?: React.SyntheticEvent) => void;
  /** Notifies the company's HR that someone wants an upgrade. The non-HR path for both CTAs. */
  onNudgeHr: (e?: React.SyntheticEvent) => void;
}

const BrochureOverlay: React.FC<BrochureOverlayProps> = ({
  open,
  onClose,
  state,
  canManageBilling,
  onSeePlans,
  onRequestUpgrade,
  onNudgeHr,
}) => {
  /*
    Direction is no longer read in JS. The two directional arrows use the `rtl:` variant, which is
    the convention in DESIGN.md and keeps the mirroring in one place instead of branching on
    `i18n.dir()` at render time.
  */
  const { t } = useTranslation();
  const navigate = useNavigate();

  const containerRef = useRef<HTMLDivElement | null>(null);
  const closeBtnRef = useRef<HTMLButtonElement | null>(null);

  // ESC closes. Focus trap keeps Tab inside the overlay while it's open.
  useEffect(() => {
    if (!open) return;

    function getFocusables(): HTMLElement[] {
      if (!containerRef.current) return [];
      const nodes = containerRef.current.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      return Array.from(nodes).filter((el) => !el.hidden && el.offsetParent !== null);
    }

    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key === "Tab") {
        const focusables = getFocusables();
        if (focusables.length === 0) {
          e.preventDefault();
          return;
        }
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        const active = document.activeElement as HTMLElement | null;
        if (e.shiftKey && active === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    document.addEventListener("keydown", onKeyDown);
    // Hand focus to the close button on open so screen readers announce
    // the takeover. A short rAF lets the overlay paint first.
    const id = window.requestAnimationFrame(() => closeBtnRef.current?.focus());
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      window.cancelAnimationFrame(id);
    };
  }, [open, onClose]);

  /*
    THE OVERLAY STAYS MOUNTED WHEN CLOSED, SO IT MUST BE TAKEN OUT OF THE TAB ORDER PROPERLY.
    `aria-hidden` alone was a lie the keyboard could disprove: `pointer-events-none` stops the
    mouse and nothing else, so a keyboard user tabbing through the ordinary page fell into ~15
    invisible controls — the close button, up to three CTAs, eight module buttons and three
    footer links — with a screen reader announcing nothing, because `aria-hidden` had removed
    exactly those controls from the accessibility tree while leaving them focusable. That pairing
    is the axe `aria-hidden-focus` violation, and it is a real trap, not a lint nit.

    Two mechanisms, deliberately belt-and-braces:
      - `invisible` (visibility: hidden) — per spec, descendants of a hidden subtree are NOT
        focusable. This works in every browser we support and is what actually fixes it.
      - `inert` — set imperatively because React 18's JSX types don't know the attribute yet.
        Where supported it also blocks programmatic focus and stray click targets.
    The fade still works: `visibility` is discretely interpolated, so it flips only once the
    opacity transition has finished.
  */
  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;
    if (open) node.removeAttribute("inert");
    else node.setAttribute("inert", "");
  }, [open]);

  const handleExploreModule = useCallback(
    (mod: BrochureModule) => {
      // Phase 6 owns the per-module brochure pages. For now we route there
      // optimistically; if the page is missing the user falls through to 404.
      onClose();
      navigate(`/brochure/${mod.slug}`);
    },
    [navigate, onClose]
  );

  // ---------- Copy variants ----------

  const headline = t("Unlock everything in Mowazaf");
  let sub: string;
  if (state.variant === "free") {
    // CO-227 — was "…here's what a trial unlocks…", offering something the product no longer has,
    // directly above its own "See plans" CTA. The plan is what unlocks these now.
    sub = t("You're on the free plan. Here's what a paid plan unlocks for your team.");
  } else if (state.variant === "trial-healthy" || state.variant === "trial-ending") {
    sub = t("Your trial ends in {{days}} days — here's everything you can still try.", {
      days: state.daysRemaining ?? 0,
    });
  } else if (state.variant === "trial-expired") {
    sub = t("Your trial ended. Choose a plan to bring your full workspace back.");
  } else {
    sub = t("Your renewal is around the corner. Here's a refresher on what's included.");
  }

  // Primary / secondary CTAs in the hero. The "Continue using free" affordance
  // is just the close button in disguise — same handler, friendlier label.
  //
  // ROLE GATE. Both of these lead somewhere only Hr/Admin may go — `/settings/billing` is a
  // RoleProtectedRoute and `POST /request-upgrade` is Hr/Admin-only — so a Manager pressing them
  // got an access-denied page or a red failure toast for doing exactly what the overlay invited.
  // The band solved this by routing non-HR roles to a nudge; the overlay does the same rather than
  // going quiet, because an upgrade surface with no action at all is its own dead end.
  //
  // 🔴 CO-227 — THE OLD `state.canStartTrial` GATE IS GONE, AND THAT CLOSES A DEAD END RATHER THAN
  // OPENING ONE. "Start free trial" could only be offered to a company the server would actually
  // grant one to, so an Hr on a Free tenant that had already spent its trial saw NO action at all
  // here while the band that opened this overlay still offered one. Seeing the plans is available
  // to every billing-capable viewer unconditionally, so the two surfaces can no longer disagree.
  const planOffer = state.variant === "free" || state.variant === "trial-expired";
  const upgradeOffer = state.variant === "trial-ending" || state.variant === "paid-expiring";

  const showSeePlans = planOffer && canManageBilling;
  const showRequestUpgrade = upgradeOffer && canManageBilling;
  const nudgeableVariant =
    state.variant === "free" ||
    state.variant === "trial-expired" ||
    state.variant === "trial-ending" ||
    state.variant === "paid-expiring";
  const showNudgeHr = nudgeableVariant && !canManageBilling;

  return (
    <div
      id="subscription-brochure-overlay"
      ref={containerRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby="brochure-headline"
      aria-hidden={!open}
      className={cn(
        // Fixed under TopBar + banner. The total offset is 64 + 44 = 108px.
        // We use inline style so the magic number is centralized here and not
        // sprinkled into Tailwind arbitrary values throughout the file.
        "fixed inset-x-0 bottom-0 z-40 overflow-y-auto",
        // Solid surface so the locked app behind doesn't bleed through. `bg-background` is the
        // same cloud-white every other in-product page sits on, which is the point: this is a
        // Mowazaf screen, not a separately-themed sales page. It was a hardcoded
        // white→slate gradient, the one surface in billing that ignored the token layer.
        "bg-background",
        // Enter/exit transitions. The overlay stays mounted between opens; see the `inert`
        // effect above for why that is safe for keyboard users.
        "transition-all duration-300 ease-out",
        open
          ? "visible opacity-100 pointer-events-auto"
          : "invisible opacity-0 pointer-events-none"
      )}
      style={{ top: TOPBAR_HEIGHT_PX + BANNER_HEIGHT_PX }}
    >
      <div className="mx-auto max-w-6xl px-4 py-10 md:px-8 md:py-14">
        {/* Close button — 40px hit target, docked to the inline-end corner so it clears the hero.
            `justify-end` is a logical alignment, so it lands top-right in English and top-left in
            Arabic without a mirrored override. */}
        <div className="flex justify-end">
          <button
            ref={closeBtnRef}
            type="button"
            onClick={onClose}
            aria-label={t("Close")}
            className={cn(
              "inline-flex h-10 w-10 items-center justify-center rounded-full",
              "border border-border bg-card text-muted-foreground shadow-sm",
              "hover:bg-accent hover:text-foreground hover:shadow-md",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2",
              "transition-colors"
            )}
          >
            <Icon name="X" size={18} aria-hidden="true" />
          </button>
        </div>

        {/* Hero — calm, one column, generous whitespace. Weight and scale carry the emphasis.
            The headline used to be a clipped gradient fill, which is a banned decoration and, on
            top of that, defeated the per-tenant primary override: the gradient was hardcoded
            indigo while every other accent on the page follows the tenant's colour. */}
        <div className="mt-2 max-w-3xl">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary">
            {t("Mowazaf for everyone")}
          </p>
          <h1
            id="brochure-headline"
            className="mt-3 text-3xl font-semibold leading-tight tracking-tight text-foreground md:text-4xl"
          >
            {headline}
          </h1>
          <p className="mt-4 max-w-[68ch] text-base leading-relaxed text-muted-foreground md:text-lg">
            {sub}
          </p>

          {/*
            EXACTLY ONE of these three ever renders — they are keyed on mutually exclusive
            variant/role combinations — so each is THE primary action of the screen and each gets
            primary weight. They previously carried three different hand-mixed fills (indigo-600,
            slate-900, slate-900), which made the non-HR nudge look like a lesser, greyed-out
            consolation for the role that has the fewest options already.
          */}
          <div className="mt-7 flex flex-wrap items-center gap-3">
            {showSeePlans && (
              <button type="button" onClick={onSeePlans} className={PRIMARY_CTA_CLASS}>
                <Icon name="Rocket" size={16} aria-hidden="true" />
                {t("See plans")}
                <Icon name="ArrowRight" size={14} className="rtl:rotate-180" aria-hidden="true" />
              </button>
            )}
            {showRequestUpgrade && (
              <button type="button" onClick={onRequestUpgrade} className={PRIMARY_CTA_CLASS}>
                <Icon name="MailPlus" size={16} aria-hidden="true" />
                {t("Request upgrade")}
              </button>
            )}
            {showNudgeHr && (
              <button type="button" onClick={onNudgeHr} className={PRIMARY_CTA_CLASS}>
                <Icon name="MailPlus" size={16} aria-hidden="true" />
                {state.variant === "paid-expiring"
                  ? t("Ask your HR to renew")
                  : t("Ask your HR to upgrade")}
              </button>
            )}
            <button type="button" onClick={onClose} className={QUIET_CTA_CLASS}>
              {state.variant === "free"
                ? t("Continue using free")
                : t("Back to my workspace")}
            </button>
          </div>
        </div>

        {/*
          What you get. This was eight identically-sized bordered cards in a 4-up grid — the
          repeated icon+heading+text tile, which is a banned shape and, worse, the wrong one for
          the job: eight equal boxes with equal chrome flatten into a wall the eye slides off,
          and the module NAMES are what a customer is actually scanning for.

          So the chrome goes and the content stays. Each module is a quiet two-column row: icon
          chip, name, pitch. Hover tints the row instead of lifting a card, which also removes an
          8-element translate-on-hover from a surface that already fades in. Still a real
          <button> per module, so keyboard navigation and the focus ring are unchanged.
        */}
        <section className="mt-14" aria-labelledby="brochure-modules-heading">
          <div className="flex items-baseline justify-between gap-4 border-b border-border pb-3">
            <h2
              id="brochure-modules-heading"
              className="text-sm font-semibold uppercase tracking-wider text-muted-foreground"
            >
              {/*
                Unlike the dashboard showcase, this overlay is NOT Free-only: `SubscriptionBanner`
                also opens it for `paid-expiring`, where the modules genuinely ARE included. So the
                heading follows the tier rather than being rewritten wholesale — locked states get
                the honest "with a paid plan" framing, entitled states keep the original claim.
              */}
              {state.variant === "free" || state.variant === "trial-expired"
                ? t("Every module, included with a paid plan")
                : t("Every module, included")}
            </h2>
            <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
              {t("{{count}} modules", { count: BROCHURE_MODULES.length })}
            </span>
          </div>

          <ul className="mt-2 grid grid-cols-1 gap-x-8 sm:grid-cols-2">
            {BROCHURE_MODULES.map((mod) => (
              <li key={mod.slug}>
                <button
                  type="button"
                  onClick={() => handleExploreModule(mod)}
                  className={cn(
                    "group flex w-full items-start gap-4 rounded-lg p-4 text-start",
                    "transition-colors duration-200 hover:bg-accent",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                  )}
                >
                  <span
                    className={cn(
                      "mt-0.5 inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg",
                      mod.accent.surface
                    )}
                  >
                    <Icon
                      name={mod.iconName}
                      size={20}
                      className={mod.accent.iconText}
                      aria-hidden="true"
                    />
                  </span>
                  <span className="min-w-0 space-y-1">
                    <span className="flex items-center gap-1.5 text-base font-semibold text-foreground">
                      {t(mod.nameKey)}
                      <Icon
                        name="ArrowRight"
                        size={13}
                        aria-hidden="true"
                        className={cn(
                          "shrink-0 text-muted-foreground opacity-0 transition-opacity",
                          "group-hover:opacity-100 group-focus-visible:opacity-100",
                          "rtl:rotate-180"
                        )}
                      />
                    </span>
                    <span className="block max-w-[62ch] text-sm leading-relaxed text-muted-foreground">
                      {t(mod.pitchKey)}
                    </span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </section>

        {/* Footer band — quiet, just two muted links so the page doesn't end abruptly. */}
        <footer className="mt-16 flex flex-wrap items-center justify-between gap-4 border-t border-border pt-6 text-xs text-muted-foreground">
          <p className="max-w-[62ch]">
            {t("Questions about what's included? We're happy to walk you through it.")}
          </p>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            {FOOTER_LINKS.map((link, i) => (
              <React.Fragment key={link.href}>
                {i > 0 && (
                  <span aria-hidden="true" className="text-muted-foreground/40">
                    ·
                  </span>
                )}
                <a
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={cn(
                    "rounded font-semibold text-foreground transition-colors hover:text-primary",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                  )}
                >
                  {t(link.labelKey)}
                </a>
              </React.Fragment>
            ))}
          </div>
        </footer>
      </div>
    </div>
  );
};

export default BrochureOverlay;
